import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { navigate } from 'gatsby';
import { useMutation } from '@apollo/react-hooks';
import { useForm } from 'react-hook-form';
// queries
import { REDEEM_APP_PROMO_CODE } from '../../queries/app';
import styles from './app-promo-code.module.scss';
// services
import { trackEvent } from '../../services/tracking/tracking';
import { formatGraphQLError } from '../../services/format';
import { buildShopifyCustomerAccountLoginUrl } from '../../services/shopify-customer-account';
// context
import { GlobalContext } from '@src/store/global-state';
//components
import SubmitButton from '../../components/submit-button';
import CustomInput from '../../components/form/custom-input';
import { getAppConfig, getPartnerConfig } from '../../constants/apps';
import Loader from '../../components/loader';

// Extend Window interface for AF_SMART_SCRIPT
declare global {
  interface Window {
    AF_SMART_SCRIPT?: {
      generateOneLinkURL: (params: any) => { clickURL: string } | null;
    };
  }
}

interface Props {
  appName: string;
  partnerId?: string;
}

const AppPromoCodeShared: React.FC<Props> = ({ appName, partnerId }) => {
  // Get app and partner configurations (only if appName is provided)
  const appConfig = appName ? getAppConfig(appName) : undefined;
  const partnerConfig = partnerId ? getPartnerConfig(partnerId) : undefined;

  // Authentication context
  const { isCheckLoginTried, isLoggedIn } = useContext(GlobalContext);

  // Redirect invalid URL combinations to the closest valid route.
  useEffect(() => {
    if (appName && !appConfig) {
      navigate('/app-promo-code', { replace: true });
      return;
    }

    if (appName && partnerId && !partnerConfig) {
      navigate(`/app-promo-code/${appName}`, { replace: true });
    }
  }, [appName, partnerId, appConfig, partnerConfig]);

  // Only show loader if we expected to find an app but didn't, or while checking login status
  if (appName && !appConfig) {
    return <Loader />;
  }

  if (!isCheckLoginTried) {
    return <Loader />;
  }

  // state
  const {
    register,
    handleSubmit,
    errors,
    formState: { dirtyFields },
    watch,
  } = useForm();
  const [generatedOneLink, setGeneratedOneLink] = useState<string>('');
  const [submittedCode, setSubmittedCode] = useState<string>('');
  const [showError, setShowError] = useState(true);
  const [isIOS, setIsIOS] = useState(false);

  // Detect iOS on client side only
  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
    }
  }, []);

  // queries
  const [
    redeemAppPromoCode,
    { data: codeRedemptionData, loading: codeRedemptionLoading, error: codeRedemptionError },
  ] = useMutation(REDEEM_APP_PROMO_CODE, {
    onCompleted: () => {
      trackEvent('Redeemed App Promo Code', {
        code: submittedCode,
        appName: appConfig?.appId,
        partnerId: partnerConfig?.partnerId,
      });
    },
  });

  const onSubmit = (formData: any) => {
    setSubmittedCode(formData.code);
    setShowError(true);
    
    trackEvent('Attempted App Promo Code Redemption', {
      code: formData.code,
      appName: appConfig?.appId,
      partnerId: partnerConfig?.partnerId,
    });

    redeemAppPromoCode({
      variables: {
        code: formData.code,
        appId: appConfig?.appId,
      },
    });
  };

  useEffect(() => {
    // Only generate OneLink if we have either a partner or app OneLink config available
    const baseOneLinkUrl = partnerConfig?.oneLinkUrl || appConfig?.oneLinkUrl;
    if (!baseOneLinkUrl) return;

    // Wait for the global object to be available
    const checkScriptLoaded = () => {
      if (window.AF_SMART_SCRIPT && typeof window.AF_SMART_SCRIPT.generateOneLinkURL === 'function') {
        const mediaSource = { defaultValue: partnerConfig?.partnerId || "any_source" };
        const custom_ss_ui = { paramKey: "af_ss_ui", defaultValue: "true" };

        const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
          oneLinkURL: baseOneLinkUrl || '',
          afParameters: {
            mediaSource: mediaSource,
            afCustom: [custom_ss_ui],
          }
        });

        console.log("OneLink generated:", result);
        // Extract the clickURL from the result object
        const clickURL = result?.clickURL || baseOneLinkUrl || '';
        setGeneratedOneLink(clickURL);
      } else {
        // Retry shortly if not loaded yet
        setTimeout(checkScriptLoaded, 100);
      }
    };

    checkScriptLoaded();
  }, [partnerConfig, appConfig]);

  // Dynamic page title and script loading
  const pageTitle = appConfig ? `Redeem ${appConfig?.displayName || 'App'} promo code` : 'Redeem promo code';
  const shouldLoadScript = Boolean(partnerConfig?.oneLinkUrl || appConfig?.oneLinkUrl);
  const promoCodePath = `/app-promo-code${appName ? `/${appName}` : ''}${
    partnerId ? `/${partnerId}` : ''
  }`;
  const customerAccountLoginUrl = buildShopifyCustomerAccountLoginUrl(promoCodePath);

  return (
    <>
    <Helmet>
      <title>{pageTitle}</title>
      {shouldLoadScript && <script src="/scripts/onelink-smart-script-latest.js" />}
    </Helmet>

    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.col}>
            {!codeRedemptionData ? (
              <div>
                <h1 className={`${styles.h1} ${!appConfig?.subtitle ? styles.h1WithoutSubtitle : ''}`}>
                  {appConfig ? `Redeem ${appConfig?.displayName || 'App'} promo code` : 'Redeem app promo code'}
                </h1>
                {appConfig?.subtitle && (
                  <p className={styles.subtitle}>{appConfig?.subtitle}</p>
                )}
                <hr />
                {(appConfig || partnerConfig) && (
                  <div className={styles.logos}>
                    {appConfig?.logoUrl && (
                      <img src={appConfig.logoUrl} alt={appConfig?.displayName || 'App'} className={styles.appLogo} />
                    )}
                    {(appConfig?.logoUrl && partnerConfig?.logoUrl) && (
                      <span className={styles.logoSeparator}>✕</span>
                    )}
                    {partnerConfig?.logoUrl && (
                      <img src={partnerConfig.logoUrl} alt={partnerConfig.displayName} className={styles.partnerLogo} />
                    )}
                  </div>
                )}

                {!isLoggedIn ? (
                  // Authentication required UI
                  <div className={styles.authRequired}>
                    <p>You must be logged in to redeem app promo codes. Please sign in to your account or create a new account to continue.</p>
                    <div className={styles.buttonContainer}>
                      <a href={customerAccountLoginUrl} className={styles.buttonPrimary}>
                        Sign In
                      </a>
                      <a href={customerAccountLoginUrl} className={styles.buttonGhost}>
                        Create Account
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                  <div className={styles.names}>
                    {appConfig ? (
                      <>Enter your promo code to get {appConfig?.displayName || 'App'}{partnerConfig ? ' with ' : ''}</>
                    ) : (
                      <>Enter your promo code</>
                    )}
                    {partnerConfig && (
                      <>{partnerConfig.displayName}</>
                    )}
                  </div>
                  {/* Promo code form (only when logged in) */}
                  <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                    <fieldset disabled={codeRedemptionLoading}>
                      <CustomInput
                        id="code"
                        register={register({ required: true })}
                        label="Promo code"
                        isDirty={dirtyFields.code || watch('code')}
                        onInput={() => setShowError(false)}
                        error={errors.code}
                        errorMessage="Please enter your promo code"
                      />
                      {showError && codeRedemptionError && (
                        <p className={styles.error}>{formatGraphQLError(codeRedemptionError.message)}</p>
                      )}
                      <br />
                      <SubmitButton
                        loading={codeRedemptionLoading}
                        buttonLabel="Redeem Promo Code"
                        fullWidth
                      />
                    </fieldset>
                  </form>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.confirmation}>
                <div className={styles.header}>
                  <h1 className={styles.h2}>Promo code redeemed</h1>
                </div>
                <hr />
                <p className={styles.unlockedOfferTitle}>You have unlocked a:</p>
                <div className={styles.unlockedOffer}>
                  {appConfig?.logoUrl && (
                    <img src={appConfig.logoUrl} alt={appConfig?.displayName || 'App'} className={styles.appLogo} />
                  )}
                  {codeRedemptionData?.app_redeemPromoCode?.offerDescription && (
                    <span>{codeRedemptionData.app_redeemPromoCode.offerDescription}</span>
                  )}
                </div>
                {appConfig ? (
                  <p className={styles.p}>Please open {appConfig?.displayName || 'App'} to start your subscription.</p>
                ) : (
                  <p className={styles.p}>You can now access your benefits.</p>
                )}
                {isIOS && Boolean(generatedOneLink || partnerConfig?.oneLinkUrl || appConfig?.oneLinkUrl) ? (
                  <a className={styles.button} href={generatedOneLink || partnerConfig?.oneLinkUrl || appConfig?.oneLinkUrl || '#'}>
                    Open App
                  </a>
                ) : (
                  <button className={styles.button} onClick={() => navigate('/account/dashboard')}>
                    Back to Dashboard
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default AppPromoCodeShared;
